'use client';

import { useState, useEffect } from 'react';
import { Anchor, Mail, Phone, Lock, Eye, EyeOff, Ship, Compass, Navigation, Loader2 } from 'lucide-react';
import { getPublicContact, type ContactInfo } from '@/services/settingsService';
import { authService } from '@/services/authService';

interface LandingPageProps {
  onDevLogin: () => void;
}

export default function LandingPage({ onDevLogin }: LandingPageProps) {
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [contact, setContact] = useState<ContactInfo>({
    phone: '',
    email: 'info@insulacharter.it',
    address: '',
    whatsapp: '',
  });

  useEffect(() => {
    getPublicContact().then(setContact).catch(() => {});
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const { user } = await authService.login(email, password);
      if (user.role !== 'ADMIN' && !user.can_bypass_landing) {
        await authService.logout();
        setError('Accesso non autorizzato');
        setTimeout(() => setError(''), 4000);
        return;
      }
      onDevLogin();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Credenziali non valide';
      setError(msg);
      setTimeout(() => setError(''), 4000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div data-cid="landing-page" className="min-h-screen bg-gradient-to-b from-sky-400 via-blue-500 to-blue-900 flex items-center justify-center px-4 relative overflow-hidden">

      {/* Cielo con sfumature */}
      <div className="absolute inset-0 bg-gradient-to-b from-sky-300/50 via-transparent to-transparent" />

      {/* Sole */}
      <div className="absolute top-10 right-10 md:top-20 md:right-20">
        <div className="w-20 h-20 md:w-32 md:h-32 bg-gradient-to-br from-yellow-200 to-orange-300 rounded-full blur-sm opacity-80 animate-pulse" />
        <div className="absolute inset-2 md:inset-4 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-full" />
      </div>

      {/* Nuvole */}
      <div className="absolute top-20 left-10 opacity-60">
        <div className="flex gap-1">
          <div className="w-16 h-8 bg-white/40 rounded-full blur-sm" />
          <div className="w-12 h-10 bg-white/40 rounded-full blur-sm -ml-4 -mt-1" />
          <div className="w-14 h-7 bg-white/40 rounded-full blur-sm -ml-3 mt-1" />
        </div>
      </div>
      <div className="absolute top-32 right-1/4 opacity-40 hidden md:block">
        <div className="flex gap-1">
          <div className="w-20 h-10 bg-white/30 rounded-full blur-sm" />
          <div className="w-16 h-12 bg-white/30 rounded-full blur-sm -ml-6 -mt-1" />
        </div>
      </div>

      {/* Bolle che salgono - valori deterministici per evitare errori di hydration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[
          { size: 12, left: 8, delay: 0, duration: 10 },
          { size: 18, left: 22, delay: 2, duration: 8 },
          { size: 10, left: 35, delay: 1, duration: 12 },
          { size: 15, left: 48, delay: 3.5, duration: 9 },
          { size: 20, left: 58, delay: 0.5, duration: 11 },
          { size: 14, left: 72, delay: 4, duration: 7 },
          { size: 16, left: 85, delay: 1.5, duration: 13 },
          { size: 11, left: 15, delay: 2.5, duration: 10 },
          { size: 19, left: 42, delay: 3, duration: 8 },
          { size: 13, left: 65, delay: 4.5, duration: 11 },
          { size: 17, left: 78, delay: 0.8, duration: 9 },
          { size: 22, left: 92, delay: 2.2, duration: 12 },
        ].map((bubble, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white/20 animate-float"
            style={{
              width: `${bubble.size}px`,
              height: `${bubble.size}px`,
              left: `${bubble.left}%`,
              bottom: `-20px`,
              animationDelay: `${bubble.delay}s`,
              animationDuration: `${bubble.duration}s`,
            }}
          />
        ))}
      </div>

      {/* Riflessi luminosi sull'acqua */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/4 w-1 h-20 bg-gradient-to-b from-white/40 to-transparent rotate-12 animate-shimmer" />
        <div className="absolute top-1/2 left-1/3 w-0.5 h-16 bg-gradient-to-b from-white/30 to-transparent -rotate-6 animate-shimmer" style={{ animationDelay: '0.5s' }} />
        <div className="absolute top-1/2 right-1/3 w-1 h-24 bg-gradient-to-b from-white/35 to-transparent rotate-3 animate-shimmer" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 right-1/4 w-0.5 h-12 bg-gradient-to-b from-white/25 to-transparent -rotate-12 animate-shimmer" style={{ animationDelay: '1.5s' }} />
      </div>

      {/* Barca decorativa a sinistra */}
      <div className="absolute bottom-32 left-8 md:left-16 opacity-20 animate-boat">
        <Ship className="w-16 h-16 md:w-24 md:h-24 text-white transform -scale-x-100" />
      </div>

      {/* Barca decorativa a destra */}
      <div className="absolute bottom-48 right-12 md:right-24 opacity-15 animate-boat" style={{ animationDelay: '2s' }}>
        <Ship className="w-12 h-12 md:w-16 md:h-16 text-white" />
      </div>

      {/* Onde animate - Layer 1 (dietro) */}
      <div className="absolute bottom-0 left-0 right-0 h-32 overflow-hidden">
        <svg
          className="absolute bottom-0 w-[200%] h-32 animate-wave"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path
            d="M0,60 C150,120 350,0 600,60 C850,120 1050,0 1200,60 L1200,120 L0,120 Z"
            className="fill-blue-800/40"
          />
        </svg>
      </div>

      {/* Onde animate - Layer 2 (medio) */}
      <div className="absolute bottom-0 left-0 right-0 h-28 overflow-hidden">
        <svg
          className="absolute bottom-0 w-[200%] h-28 animate-wave-slow"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path
            d="M0,80 C200,40 400,100 600,60 C800,20 1000,80 1200,40 L1200,120 L0,120 Z"
            className="fill-blue-900/50"
          />
        </svg>
      </div>

      {/* Onde animate - Layer 3 (davanti) */}
      <div className="absolute bottom-0 left-0 right-0 h-24 overflow-hidden">
        <svg
          className="absolute bottom-0 w-[200%] h-24 animate-wave-fast"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path
            d="M0,40 C150,80 350,20 500,60 C650,100 850,40 1000,80 C1100,100 1150,60 1200,40 L1200,120 L0,120 Z"
            className="fill-blue-950/60"
          />
        </svg>
      </div>

      {/* Contenuto principale */}
      <div className="max-w-2xl w-full text-center relative z-10">

        {/* Logo con effetto onda */}
        <div className="mb-8 flex justify-center">
          <div className="relative">
            <img src="/logo-h.png" alt="InsulaCharter - Noleggio Imbarcazioni in Sardegna" className="h-16 md:h-20 w-auto object-contain drop-shadow-lg" style={{ filter: 'brightness(0) invert(1)' }} loading="eager" fetchPriority="high" />
            {/* Bussola decorativa */}
            <div className="absolute -right-4 -top-4 bg-white/10 p-2 rounded-full border border-white/20 animate-spin-slow">
              <Compass className="w-6 h-6 text-white/70" />
            </div>
          </div>
        </div>

        <h1 className="sr-only">InsulaCharter - Piattaforma di Noleggio Imbarcazioni in Sardegna</h1>

        {/* Tagline con icona */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <Navigation className="w-4 h-4 text-sky-200" />
          <p className="text-sm md:text-base text-sky-200 font-bold uppercase tracking-[0.3em]">
            Sardegna
          </p>
          <Navigation className="w-4 h-4 text-sky-200 rotate-180" />
        </div>

        <p className="text-lg text-blue-100 mb-12 max-w-xl mx-auto leading-relaxed">
          Stiamo migliorando il sito per offrirti un&apos;esperienza ancora migliore.<br />
          <span className="font-bold text-white">Saremo di nuovo online a breve!</span>
        </p>

        {/* Card contatti con effetto vetro */}
        <div className="bg-white/10 backdrop-blur-md rounded-[2rem] p-8 mb-8 border border-white/20 shadow-2xl">
          <h2 className="text-xl font-bold text-white mb-6 flex items-center justify-center gap-2">
            <span className="w-8 h-px bg-white/30" />
            Hai bisogno di informazioni?
            <span className="w-8 h-px bg-white/30" />
          </h2>

          <div className="space-y-4">
            <a
              href={`mailto:${contact.email}`}
              className="flex items-center justify-center gap-3 text-blue-100 hover:text-white transition-all hover:scale-105 group"
            >
              <div className="p-2 bg-white/10 rounded-full group-hover:bg-white/20 transition-colors">
                <Mail className="w-5 h-5" />
              </div>
              <span className="font-medium">{contact.email}</span>
            </a>

            {contact.phone && (
              <a
                href={`tel:${contact.phone.replace(/\s/g, '')}`}
                className="flex items-center justify-center gap-3 text-blue-100 hover:text-white transition-all hover:scale-105 group"
              >
                <div className="p-2 bg-white/10 rounded-full group-hover:bg-white/20 transition-colors">
                  <Phone className="w-5 h-5" />
                </div>
                <span className="font-medium">{contact.phone}</span>
              </a>
            )}

            {contact.whatsapp && (
              <a
                href={`https://wa.me/${contact.whatsapp.replace(/[\s+]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 text-blue-100 hover:text-white transition-all hover:scale-105 group"
              >
                <div className="p-2 bg-white/10 rounded-full group-hover:bg-white/20 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.111.547 4.099 1.504 5.832L0 24l6.335-1.652A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818c-1.855 0-3.622-.508-5.158-1.39l-.36-.214-3.742.977.998-3.648-.235-.374A9.772 9.772 0 012.182 12c0-5.414 4.404-9.818 9.818-9.818S21.818 6.586 21.818 12 17.414 21.818 12 21.818z"/></svg>
                </div>
                <span className="font-medium">WhatsApp</span>
              </a>
            )}
          </div>
        </div>

        <p className="text-sm text-blue-200/70">
          © 2026 InsulaCharter - Piattaforma di Noleggio Imbarcazioni in Sardegna
        </p>
      </div>

      {/* Dev Login Button */}
      <button
        onClick={() => setShowLoginForm(true)}
        className="fixed bottom-6 right-6 bg-white/10 hover:bg-white/20 backdrop-blur-sm p-3 rounded-full transition-all border border-white/30 hover:scale-110 hover:rotate-12 z-20"
        title="Accesso sviluppatore"
      >
        <Lock className="w-5 h-5 text-white" />
      </button>

      {/* Dev Login Modal */}
      {showLoginForm && (
        <div className="fixed inset-0 bg-blue-900/70 backdrop-blur-sm flex items-center justify-center px-4 z-50">
          <div className="bg-white rounded-[2rem] shadow-2xl p-8 max-w-md w-full animate-in zoom-in-95 duration-300">
            <div className="flex justify-center mb-6">
              <div className="bg-gradient-to-br from-blue-500 to-blue-700 p-4 rounded-full shadow-lg shadow-blue-500/30">
                <Lock className="w-8 h-8 text-white" />
              </div>
            </div>

            <h2 className="text-2xl font-black text-center text-slate-900 mb-2">
              Accesso Amministratore
            </h2>
            <p className="text-center text-slate-500 mb-6 font-medium">
              Accedi con il tuo account admin per visualizzare il sito
            </p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4 text-sm font-bold animate-in shake duration-300">
                {error}
              </div>
            )}

            <form onSubmit={handleLogin}>
              <div className="mb-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className="w-full px-4 py-4 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none font-medium transition-all"
                  required
                  autoFocus
                  disabled={isLoading}
                />
              </div>

              <div className="relative mb-4">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full px-4 py-4 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none pr-12 font-medium transition-all"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowLoginForm(false);
                    setEmail('');
                    setPassword('');
                    setError('');
                  }}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-3.5 rounded-xl font-bold transition-all active:scale-95"
                  disabled={isLoading}
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white py-3.5 rounded-xl font-bold transition-all shadow-lg shadow-blue-500/30 active:scale-95 disabled:opacity-60"
                  disabled={isLoading}
                >
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Accedi'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSS per animazioni personalizzate */}
      <style jsx>{`
        @keyframes wave {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes float {
          0%, 100% {
            transform: translateY(0) scale(1);
            opacity: 0;
          }
          10% { opacity: 0.6; }
          90% { opacity: 0.6; }
          100% {
            transform: translateY(-100vh) scale(0.5);
            opacity: 0;
          }
        }
        @keyframes shimmer {
          0%, 100% { opacity: 0.3; transform: scaleY(1); }
          50% { opacity: 0.6; transform: scaleY(1.2); }
        }
        @keyframes boat {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          25% { transform: translateY(-8px) rotate(2deg); }
          75% { transform: translateY(4px) rotate(-2deg); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-wave {
          animation: wave 8s linear infinite;
        }
        .animate-wave-slow {
          animation: wave 12s linear infinite;
        }
        .animate-wave-fast {
          animation: wave 6s linear infinite;
        }
        .animate-float {
          animation: float 10s ease-in-out infinite;
        }
        .animate-shimmer {
          animation: shimmer 3s ease-in-out infinite;
        }
        .animate-boat {
          animation: boat 4s ease-in-out infinite;
        }
        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
      `}</style>
    </div>
  );
}
